/* eslint-disable */

/**
 * Where something on the product list (Products.vue) is sold out.
 *
 * A sold-out mark belongs to ONE location: the tills, the kiosks and the webshop each read only their own
 * location's marks, so a library add-on that ran out at one location is still sold at the others. The
 * product list used to pool every location's marks into one set of ids and show a bare «Utsolgt», which
 * made a mark for one location look like it applied to all of them. These helpers keep the location
 * attached all the way to the badge.
 */
class SoldOutUtil {
    /**
     * The sold-out marks indexed by what they mark, each mapped to the locations it is sold out at:
     * {items: {itemId: [locationId]}, subselections: {...}, templates: {...}, locationIds, locationNames}.
     *
     * locationIds is what the page is showing: the chosen location, or every location. Marks outside it are
     * left out, and so are marks for a location that no longer exists, since nothing sells there. If the
     * locations could not be loaded, every mark counts and the badge cannot name a location.
     */
    index(soldOutItems, locations, locationFilter){
        let knownIds = [];
        let locationNames = {};
        for(let i = 0; i < (locations || []).length; i++){
            knownIds.push(locations[i].id);
            locationNames[locations[i].id] = locations[i].name;
        }
        let scope = locationFilter ? [locationFilter] : knownIds;
        let index = { items: {}, subselections: {}, templates: {}, locationIds: scope.slice(), locationNames: locationNames };

        for(let i = 0; i < (soldOutItems || []).length; i++){
            let mark = soldOutItems[i];
            if(scope.length > 0 && scope.indexOf(mark.locationId) === -1){
                continue;
            }
            if(mark.itemId){
                this.add(index.items, mark.itemId, mark.locationId);
            } else if(mark.subselectionId){
                this.add(index.subselections, mark.subselectionId, mark.locationId);
            } else if(mark.templateId){
                this.add(index.templates, mark.templateId, mark.locationId);
            } else {
                continue;
            }
            if(scope.length === 0 && index.locationIds.indexOf(mark.locationId) === -1){
                index.locationIds.push(mark.locationId);
            }
        }
        return index;
    }

    add(bucket, id, locationId){
        if(!bucket[id]){
            bucket[id] = [];
        }
        if(bucket[id].indexOf(locationId) === -1){
            bucket[id].push(locationId);
        }
    }

    /**
     * The locations an item is sold out at: marked itself, or every one of its variants marked there.
     * Variants have to be sold out at the SAME location. Two variants sold out at two different locations
     * leave the item for sale at both.
     */
    itemSoldOutAt(item, index){
        if(!index){
            return [];
        }
        let subselections = item.subselections || [];
        return index.locationIds.filter(locationId => {
            if((index.items[item.id] || []).indexOf(locationId) > -1){
                return true;
            }
            return subselections.length > 0 && subselections.every(subselection =>
                (index.subselections[subselection.id] || []).indexOf(locationId) > -1);
        });
    }

    /**
     * The locations an add-on is sold out at: through the product it is linked to, or through its
     * library entry. The same two checks the tills and the webshop make, per location.
     */
    addOnSoldOutAt(addOn, index){
        if(!index){
            return [];
        }
        return index.locationIds.filter(locationId =>
            (!!addOn.itemId && (index.items[addOn.itemId] || []).indexOf(locationId) > -1)
            || (!!addOn.templateId && (index.templates[addOn.templateId] || []).indexOf(locationId) > -1));
    }

    itemBadge(item, index){
        return this.badge(this.itemSoldOutAt(item, index), index);
    }

    addOnBadge(addOn, index){
        return this.badge(this.addOnSoldOutAt(addOn, index), index);
    }

    /**
     * {label, title} for the «Utsolgt» badge, or null when it is for sale everywhere the page is showing.
     *
     * The bare «Utsolgt» is kept for when it says everything: one location in view, or sold out at every
     * location. Otherwise the label names where it is sold out («Utsolgt: Grefsen»), because a bare
     * «Utsolgt» over «Alle avdelinger» reads as sold out everywhere.
     */
    badge(soldOutAt, index){
        if(soldOutAt.length === 0){
            return null;
        }
        let names = soldOutAt.map(locationId => index.locationNames[locationId]);
        let namesKnown = names.every(name => !!name);
        if(soldOutAt.length === index.locationIds.length){
            let title = index.locationIds.length > 1 ? 'Utsolgt på alle avdelinger'
                : namesKnown ? 'Utsolgt på ' + names[0] : 'Utsolgt';
            return { label: 'Utsolgt', title: title };
        }
        if(!namesKnown){
            return { label: 'Utsolgt', title: 'Utsolgt på noen avdelinger' };
        }
        return {
            label: 'Utsolgt: ' + names.join(', '),
            title: 'Utsolgt på ' + names.join(', ') + '. De andre avdelingene selger det som vanlig.'
        };
    }
}

export default new SoldOutUtil();
